/**
 * Hook for AI Translator settings with instant UI updates and debounced DB saves
 * Handles conversion between database format (snake_case) and local format (camelCase)
 */

/* eslint-disable @typescript-eslint/no-unnecessary-condition */

import { useCallback, useEffect, useRef, useState } from "react"

import debounce from "lodash/debounce"

import type { AITranslatorSettings } from "../constants/translator-defaults"

import { useAITranslatorSettings as useAITranslatorSettingsDB } from "@/hooks/use-module-settings"

import { defaultSettings } from "../constants/translator-defaults"

// Convert database format (snake_case) to local format (camelCase)
function dbToLocal(dbSettings: Record<string, unknown> | undefined): AITranslatorSettings {
  if (!dbSettings) return defaultSettings

  // Using ?? operator to only replace null/undefined, not empty arrays/strings
  return {
    availableLanguages:
      (dbSettings.available_languages as string[]) ?? defaultSettings.availableLanguages,
    selectedLanguage: (dbSettings.selected_language as string) ?? defaultSettings.selectedLanguage,
    selectedLanguages: (dbSettings.selected_languages as string[]) ?? [
      (dbSettings.selected_language as string) ?? defaultSettings.selectedLanguage,
    ],
    translationInstructions:
      (dbSettings.translation_instructions as string) ?? defaultSettings.translationInstructions,
    customTranslations:
      (dbSettings.custom_translations as Array<{ original: string; translation: string }>) ??
      defaultSettings.customTranslations,
  }
}

// Convert local format (camelCase) to database format (snake_case)
// Always converts ALL fields to ensure complete data in database
function localToDb(localSettings: AITranslatorSettings): Record<string, unknown> {
  return {
    available_languages: localSettings.availableLanguages,
    selected_language: localSettings.selectedLanguage,
    selected_languages: localSettings.selectedLanguages ?? [localSettings.selectedLanguage],
    translation_instructions: localSettings.translationInstructions,
    custom_translations: localSettings.customTranslations,
  }
}

export function useAITranslatorSettingsLocal() {
  const { settings: dbSettings, updateSettings: updateDB } = useAITranslatorSettingsDB()

  // Local state for instant UI updates (camelCase)
  const [localSettings, setLocalSettings] = useState<AITranslatorSettings>(() =>
    dbToLocal(dbSettings as Record<string, unknown> | undefined)
  )

  // Sync local state with database when DB settings change
  useEffect(() => {
    if (dbSettings) {
      setLocalSettings(dbToLocal(dbSettings as unknown as Record<string, unknown>))
    }
  }, [dbSettings])

  // Debounced database save (500ms)
  const debouncedSave = useRef(
    debounce((settings: Record<string, unknown>) => {
      updateDB(settings as Partial<Record<string, unknown>>)
    }, 500)
  )

  // Update function: instant local state update + debounced DB save
  const updateSettings = useCallback((updates: Partial<AITranslatorSettings>) => {
    // 1. Update local state immediately for instant UI feedback
    setLocalSettings((prev) => {
      const newSettings = { ...prev, ...updates }

      // 2. Convert FULL settings to database format and debounce the save
      // This ensures we don't overwrite other fields
      const dbFormat = localToDb(newSettings)
      debouncedSave.current(dbFormat)

      return newSettings
    })
  }, [])

  return {
    settings: localSettings,
    updateSettings,
  }
}
